import { GRAPHQL_ENDPOINT, GET_RENTAL_QUERY } from "./resources.js?v=1.5";
import { addItemsToDOM, removeItemsFromDOM, rentalCardHTML } from "./utilities.js?v=1.0";

/**
  * Fetches rental packages from the reservation system for every rental panel on the page
  * (sections/rental-packages.liquid). Each panel supplies its own store (via the Store
  * Location metaobject selected in the section settings), an optional rental-system category
  * id, and a package type (all / daily / seasonal) through data attributes rendered by the
  * section.
  *
  * Daily and Seasonal are shown as two separate section instances (rather than one section
  * with two panels) - so if both are placed on the same page for the same store, this dedupes
  * the network call: only one request per store+category is made and both panels read from it.
  * @return Adds the Packages to DOM for every rental panel found on the page
*/

// storeId+categoryId -> in-flight/completed fetch, so Daily and Seasonal instances of this
// section for the same store don't each make their own request to the rental system.
const rentalRequestCache = new Map();

(() => {
  const panels = document.querySelectorAll('[data-rental-panel]');
  panels.forEach(fetchRentalsForPanel);
})();

/**
  * Fetches and renders the rentals for a single panel element.
  * @param panel (Element carrying data-store-id / data-category-id / data-package-type / data-target / data-section-root)
*/
function fetchRentalsForPanel(panel) {
  const storeId = Number(panel.dataset.storeId);
  const categoryId = panel.dataset.categoryId ? Number(panel.dataset.categoryId) : null;
  const packageType = panel.dataset.packageType || 'all'; // 'all' | 'daily' | 'seasonal'

  if (!storeId) {
    if (panel.dataset.storeHandle) {
      // A store metaobject IS selected, but its rental_system_store_id field didn't resolve -
      // most likely that field is empty on this particular entry, or its key was renamed.
      console.log(
        `Rental panel: store "${panel.dataset.storeHandle}" is selected, but its rental_system_store_id field is empty or missing - check the Store Location metaobject entry/definition in the Shopify admin.`
      );
    } else {
      // No store metaobject selected in the section settings yet - nothing to fetch.
      console.log('Rental panel is missing a store - select a Store in the section settings.');
    }
    return;
  }

  getRentalsForStore(storeId, categoryId)
    .then(({ data }) => {
      if (packageType === 'daily' || packageType === 'seasonal') {
        const packages = splitByType(data.getRentals.data, data.getRentals.discount);

        if (packageType === 'seasonal') {
          if (data.getRentals.season !== 'winter') {
            // Same behavior as the old combined rentals page: seasonal packages are only
            // offered in winter - hide the whole section rather than show an empty grid.
            if (panel.dataset.sectionRoot) {
              removeItemsFromDOM(panel.dataset.sectionRoot);
            }
            return;
          }
          addItemsToDOM(packages.seasonal, panel.dataset.target, rentalCardHTML);
        } else {
          addItemsToDOM(packages.daily, panel.dataset.target, rentalCardHTML);
        }
      } else {
        // "all" - no Daily/Seasonal split, render whatever the rental system returned.
        const items = (data.getRentals.data || []).map((item) => ({
          ...item,
          discount: data.getRentals.discount,
        }));
        addItemsToDOM(items, panel.dataset.target, rentalCardHTML);
      }

      // Initialize any Tooltip on this page
      $(document).ready(function ()
          {
              $('[data-toggle="tooltip"]').tooltip();
          }
      );
    }).catch((error) => {
      console.log(error.message)
    });
}

/**
  * Returns a (possibly shared/cached) promise resolving to the parsed GraphQL response for
  * a given store + category, so multiple panels targeting the same store don't each fire
  * their own request.
*/
function getRentalsForStore(storeId, categoryId) {
  const cacheKey = `${storeId}:${categoryId ?? ''}`;

  if (!rentalRequestCache.has(cacheKey)) {
    const variables = { storeId };
    if (categoryId) {
      variables.categoryId = categoryId;
    }

    const request = fetch(GRAPHQL_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ query: GET_RENTAL_QUERY, variables }),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText || "Failed to fetch");
      }
      return response.json();
    });

    rentalRequestCache.set(cacheKey, request);
  }

  return rentalRequestCache.get(cacheKey);
}

/**
  * Splits the raw rental-system response into Daily / Seasonal packages: Daily packages keep
  * the store's real discount, Seasonal packages always show a 0 discount.
  * @param rentalData (Array of Packages)
  * @param discount (Discount on a particular store)
  * @return { daily: [...], seasonal: [...] }
*/
function splitByType(rentalData, discount) {
  const packages = { daily: [], seasonal: [] };
  (rentalData || []).forEach((item) => {
    if (item?.type === 'D') {
      packages.daily.push({ ...item, discount });
    } else {
      // TODO: make the seasonal discount dynamic based on store from admin settings
      packages.seasonal.push({ ...item, discount: 0 });
    }
  });
  return packages;
}